import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveAgentId,
  selectActiveChatId,
  selectArtifactStreamingState,
  markArtifactContentProcessed,
} from './agentsChatsReducer';
import { useArtifacts } from '../common/artifacts/useArtifacts';
import { ArtifactData } from '../common/ai-chat/aiChatTypes';
import { useAgentChatInstance } from './AgentChatInstanceContext';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';

/**
 * Hook to handle artifact streaming for the active chat.
 *
 * This hook subscribes to the artifact streaming state in Redux and:
 * 1. Initiates streaming when artifact metadata arrives
 * 2. Incrementally adds content to the artifact panel as chunks arrive
 * 3. Completes streaming and renders the artifact when done
 *
 * IMPORTANT: Should only be called ONCE per chat instance (in AgentChat component)
 * to prevent duplicate content processing.
 *
 * Uses `lastProcessedLength` in Redux as a safeguard against duplicates.
 */
export const useArtifactStreaming = () => {
  const dispatch = useDispatch();
  const { chatInstanceId } = useAgentChatInstance();
  const activeAgentId = useSelector(selectActiveAgentId);
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const artifactStreamingState = useSelector(selectArtifactStreamingState(activeChatId ?? ''));
  const { artifacts: artifactsFeatureFlag } = useFeatureToggle();

  const {
    startArtifactStreaming,
    addStreamingContent,
    completeArtifactStreaming,
    addArtifactData,
  } = useArtifacts();

  const hasStartedStreaming = useRef(false);

  // Track and handle artifact streaming - using Redux state as source of truth
  useEffect(() => {
    // Only process if feature flag is enabled
    if (!artifactsFeatureFlag) {
      return;
    }
    if (!artifactStreamingState) {
      hasStartedStreaming.current = false;
      return;
    }

    const { artifactId, versionId, accumulatedContent, lastProcessedLength, isComplete } =
      artifactStreamingState;
    const currentLength = accumulatedContent.length;

    // Artifact just started (metadata arrived)
    if (!hasStartedStreaming.current && artifactId) {
      startArtifactStreaming(artifactId);
      hasStartedStreaming.current = true;
    }

    // Artifact content is being streamed - only process new content
    if (currentLength > lastProcessedLength) {
      const newContent = accumulatedContent.substring(lastProcessedLength);
      addStreamingContent(newContent);

      // Mark this content as processed in Redux
      if (activeAgentId && activeChatId) {
        dispatch(
          markArtifactContentProcessed({
            agentId: activeAgentId,
            chatId: activeChatId,
            processedLength: currentLength,
          }),
        );
      }
    }

    // Artifact streaming completed
    if (isComplete && currentLength > 0) {
      // Infer content type from code markers
      const inferContentType = (code: string): 'react' | 'html' | 'javascript' => {
        // Remove the opening code fence to check the language tag
        const firstLine = code.split('\n')[0];
        if (firstLine.includes('jsx') || firstLine.includes('tsx')) {
          return 'react';
        }
        if (firstLine.includes('html')) {
          return 'html';
        }
        // Default to react for most artifacts
        return 'react';
      };

      // Clean up the artifact content (remove code fences)
      const cleanContent = accumulatedContent
        .replace(/^```[a-z]*\n/i, '') // Remove opening fence
        .replace(/\n```\s*$/i, ''); // Remove closing fence

      const contentType = inferContentType(accumulatedContent);

      // Add artifact data to the artifacts store
      const artifactData: ArtifactData = {
        artifactId,
        versions: [
          {
            versionId: versionId,
            title: 'Version 1',
            content: cleanContent,
            contentType,
            createdAt: new Date().toISOString(),
          },
        ],
        currentVersion: versionId,
      };

      addArtifactData(artifactId, artifactData);
      completeArtifactStreaming();
    }
  }, [
    artifactStreamingState,
    activeAgentId,
    activeChatId,
    dispatch,
    startArtifactStreaming,
    addStreamingContent,
    completeArtifactStreaming,
    addArtifactData,
    artifactsFeatureFlag,
  ]);
};
